import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, Loader2, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel } from "@/components/PageHeader";
import { METRICS, classify } from "@/components/flyers/StoreTable";
import { api, eur, errMsg, fdate } from "@/lib/api";
import { downloadCsv, nfmt, pctFmt } from "@/lib/csv";

// [key, label, fmt, perDay, higherIsBetter]
const ROWS = [["revenue", "Incasso promo", eur, true, true], ["pieces", "Pezzi venduti", (v) => nfmt(v, 0), true, true], ["receipts", "Scontrini con promo", (v) => nfmt(v, 0), true, true],
  ["chain_share", "% scontrini catena", (v) => `${nfmt(v, 2)}%`, false, true], ["cards", "Carte fedeltà", (v) => nfmt(v, 0), true, true],
  ["avg_price", "Prezzo medio effettivo", eur, false, null], ["price_gap", "Scostamento medio prezzo effettivo vs promo", (v) => pctFmt(v), false, null],
  ["below", "Sedi sotto media", (v) => nfmt(v, 0), false, false], ["none", "Sedi che non vendono", (v) => nfmt(v, 0), false, false]];

export default function FlyerCompare() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const ids = params.get("ids") || "";
  const [compare, setCompare] = useState("prev");
  const [metric, setMetric] = useState("pct_receipts");
  const [thr, setThr] = useState(20);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let live = true; setErr("");
    api.get("/flyers-compare", { params: { ids, compare } }).then((r) => live && setD(r.data)).catch((e) => live && setErr(errMsg(e)));
    return () => { live = false; };
  }, [ids, compare]);
  const cols = useMemo(() => (d?.flyers || []).map((f) => {
    const g = classify(f.stores, f.chain, metric, thr);
    return { ...f, v: { ...f.kpi, price_gap: f.price_gap, below: g.below.length, none: g.none.length } };
  }), [d, metric, thr]);
  const best = (key, perDay, hib) => {
    if (hib === null || cols.length < 2) return null;
    const ok = cols.map((c) => c.v.revenue > 0);
    if (ok.filter(Boolean).length < 2) return null;
    const vals = cols.map((c, i) => (!ok[i] ? (hib ? -Infinity : Infinity) : perDay ? (c.v[key] || 0) / c.days : c.v[key] ?? 0));
    const t = hib ? Math.max(...vals) : Math.min(...vals);
    return vals.indexOf(t);
  };
  const csv = () => downloadCsv("confronto-volantini", ["Indicatore", ...cols.flatMap((c) => [`${c.flyer.name} totale`, `${c.flyer.name} al giorno`])],
    [["Giorni di validità", ...cols.flatMap((c) => [c.days, ""])], ...ROWS.map(([k, l, , pd]) => [l, ...cols.flatMap((c) => [c.v[k], pd ? Math.round(((c.v[k] || 0) / c.days) * 100) / 100 : ""])])]);
  return (
    <div data-testid="flyer-compare-page">
      <button type="button" className="mb-3 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="flyer-compare-back" onClick={() => nav("/volantini-promo")}><ArrowLeft className="h-4 w-4" />Tutti i volantini</button>
      <PageHeader title="Confronto volantini" subtitle="Totali e media al giorno affiancati: il trofeo indica il volantino migliore per ogni indicatore">
        {d && <Button variant="outline" data-testid="flyer-compare-csv" onClick={csv}><Download className="mr-1 h-4 w-4" />CSV</Button>}
      </PageHeader>
      <Panel className="mb-6" testId="flyer-compare-controls">
        <div className="grid gap-3 p-4 md:grid-cols-3">
          <label className="text-xs text-slate-500">Variazioni rispetto a
            <Select value={compare} onValueChange={setCompare}><SelectTrigger className="mt-1" data-testid="flyer-compare-mode"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="prev">Periodo precedente di pari durata</SelectItem><SelectItem value="yoy">Stesso periodo anno prima</SelectItem></SelectContent></Select></label>
          <label className="text-xs text-slate-500">Misura sedi vs media
            <Select value={metric} onValueChange={setMetric}><SelectTrigger className="mt-1" data-testid="flyer-compare-metric"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(METRICS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></label>
          <label className="text-xs text-slate-500">Sotto media se inferiore di oltre (%)
            <Input type="number" min={0} max={100} className="mt-1" data-testid="flyer-compare-threshold" value={thr} onChange={(e) => setThr(Math.max(0, Math.min(100, Number(e.target.value) || 0)))} /></label>
        </div>
      </Panel>
      {err && <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="flyer-compare-error">{err}</div>}
      {!d && !err && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>}
      {d && <Panel testId="flyer-compare-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: 260 + cols.length * 260 }}>
            <thead><tr className="border-b border-slate-100"><th className="sticky left-0 z-10 bg-white px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">Indicatore</th>
              {cols.map((c) => <th key={c.flyer.id} colSpan={2} className="px-3 py-3 text-left" data-testid={`flyer-compare-col-${c.flyer.id}`}>
                <div className="font-heading font-semibold text-slate-900">{c.flyer.name}</div>
                <div className="text-[11px] font-normal text-slate-500">{fdate(c.flyer.start)} – {fdate(c.flyer.end)} · {c.days} giorni · {c.matched} prodotti</div></th>)}</tr>
              <tr className="text-[11px] uppercase tracking-wider text-slate-400"><th className="sticky left-0 z-10 bg-white" />{cols.map((c) => [<th key="t" className="px-3 pb-2 text-right font-medium">Totale</th>, <th key="g" className="px-3 pb-2 text-right font-medium">Al giorno</th>])}</tr></thead>
            <tbody>{ROWS.map(([k, l, fmt, pd, hib]) => {
              const bt = best(k, false, hib), bd = pd ? best(k, true, hib) : null;
              return (
                <tr key={k} className="border-t border-slate-100" data-testid={`flyer-compare-row-${k}`}>
                  <td className="sticky left-0 z-10 bg-white px-5 py-2.5 text-slate-700">{l}</td>
                  {cols.map((c, i) => [
                    <td key="t" className={`px-3 py-2.5 text-right tabular-nums ${bt === i ? "font-semibold text-emerald-700" : ""}`}>
                      {bt === i && <Trophy className="mr-1 inline h-3.5 w-3.5" />}{fmt(c.v[k])}
                      {c.kpi_delta[k] !== undefined && <div className="text-[10px] font-normal text-slate-400">{pctFmt(c.kpi_delta[k])} vs {compare === "yoy" ? "anno prima" : "prec."}</div>}</td>,
                    <td key="g" className={`px-3 py-2.5 text-right tabular-nums ${bd === i ? "font-semibold text-emerald-700" : "text-slate-500"}`}>
                      {pd ? <>{bd === i && <Trophy className="mr-1 inline h-3.5 w-3.5" />}{fmt((c.v[k] || 0) / c.days)}</> : "—"}</td>])}
                </tr>);
            })}</tbody>
          </table>
        </div>
      </Panel>}
    </div>
  );
}
